// The Rules page's working copy of a point based model, and what it can say
// about it without asking the server.
//
// Plain data and pure functions, so the page can recompute ranges and hold an
// unfinished rule on every keystroke, and so the rules are testable without a
// DOM. The shapes are the stored ones: `GroupPointBasedRulesDataModel` and its
// rules, with an optional `name`.
import isEqual from 'lodash/isEqual';

import GroupPointBasedRulesDataModel from '../../../models/pointBasedForm/GroupPointBasedRulesDataModel';
import PointBasedConditionDataModel from '../../../models/pointBasedForm/PointBasedConditionDataModel';
import PointBasedRuleDataModel from '../../../models/pointBasedForm/PointBasedRuleDataModel';
import { pointsRange } from '../../../models/pointBasedForm/pointsRange';
import { getVerbSettings, Subject } from '../../../models/sqlform';
import { computationDisplayName } from '../../../utils';

/**
 * A rule or group on the page carries `_key`, a client-side id that dragging
 * needs and the stored document does not have. Added on load, stripped before
 * every write, so what is stored is exactly what the old page stores.
 */
export type KeyedRule = PointBasedRuleDataModel & { _key?: string };

export type PointRuleGroup = Pick<
  GroupPointBasedRulesDataModel,
  'number' | 'name'
> & { rules: KeyedRule[]; _key?: string };

let keySeq = 0;
const nextKey = () => {
  keySeq += 1;
  return `k${keySeq}`;
};

export function withKeys(groups: PointRuleGroup[]): PointRuleGroup[] {
  return groups.map((group) => ({
    ...group,
    _key: group._key ?? nextKey(),
    rules: (group.rules ?? []).map((rule) => ({
      ...rule,
      _key: rule._key ?? nextKey(),
    })),
  }));
}

/** A copy without its `_key`, the one field the stored document never had. */
function unkeyed<T extends { _key?: string }>(item: T): T {
  const copy = { ...item };
  delete copy._key;
  return copy;
}

export function withoutKeys(groups: PointRuleGroup[]): PointRuleGroup[] {
  return groups.map((group) => ({
    ...unkeyed(group),
    rules: (group.rules ?? []).map(unkeyed),
  }));
}

/** A new rule or group gets its key when it is made. */
export function keyed<T extends object>(item: T): T & { _key: string } {
  return { ...item, _key: nextKey() };
}

/** What the group is called on screen: its name, else what its first rule reads. */
export function groupLabel(group: PointRuleGroup, index: number): string {
  const name = group.name?.trim();
  if (name) return name;
  const subject = group.rules?.[0]?.conditions?.[0]?.trait?.subject;
  return subject ? computationDisplayName(subject) : `Group ${index + 1}`;
}

/** The points one group can add, 0 included: a group whose rules all miss adds nothing. */
export function groupRange(group: PointRuleGroup) {
  return pointsRange([group]);
}

/** A condition the scoring SQL cannot be written from yet. */
export function isConditionUnfinished(
  condition: PointBasedConditionDataModel
): boolean {
  const settings = getVerbSettings(condition.verb);
  if (!condition.trait || !settings) return true;
  const values = (condition.values ?? []).map((value) =>
    String(value ?? '').trim()
  );
  if (values.filter(Boolean).length < settings.characteristics.min) return true;
  if (values.some((value) => !value)) return true;
  return (
    condition.trait.type === 'number' &&
    values.some((value) => !Number.isFinite(Number(value)))
  );
}

export function isRuleUnfinished(rule: PointBasedRuleDataModel): boolean {
  return (
    !Number.isFinite(Number(rule.points)) ||
    !rule.conditions?.length ||
    rule.conditions.some(isConditionUnfinished)
  );
}

/**
 * Every rule that would fail the save, by position.
 *
 * The save compiles the whole document to SQL and validates it by running it,
 * so one unfinished rule fails the request and rolls back every finished edit
 * with it. The page holds the save instead and names these.
 */
export function unfinishedRules(
  groups: PointRuleGroup[]
): { group: number; rule: number }[] {
  return groups.flatMap((group, groupIndex) =>
    (group.rules ?? [])
      .map((rule, ruleIndex) =>
        isRuleUnfinished(rule) ? { group: groupIndex, rule: ruleIndex } : null
      )
      .filter(Boolean)
  );
}

/**
 * Why the document cannot be written yet, or null when it can.
 *
 * `unfinished`: a rule the SQL cannot be written from. `empty`: no group holds a
 * rule, and no rules compile to no SQL at all, which the save always refuses.
 */
export function holdReason(
  groups: PointRuleGroup[]
): 'unfinished' | 'empty' | null {
  if (unfinishedRules(groups).length) return 'unfinished';
  if (!groups.some((group) => group.rules?.length)) return 'empty';
  return null;
}

/** A condition on a field, waiting for its value: held, never saved blank. */
export function newCondition(field: Subject): PointBasedConditionDataModel {
  const settings = getVerbSettings('is');
  return {
    trait: field,
    verb: 'is',
    values: Array(settings?.characteristics.min ?? 1).fill(''),
    isLower: false,
  } as PointBasedConditionDataModel;
}

export function newRule(field: Subject): PointBasedRuleDataModel {
  return { conditions: [newCondition(field)], points: 0 };
}

/**
 * One rule moved up or down within its group, as a new document.
 *
 * Order is the rule: the first rule that matches decides the group's points,
 * so a rule's position is part of what it means.
 */
export function moveRule(
  groups: PointRuleGroup[],
  groupIndex: number,
  ruleIndex: number,
  direction: -1 | 1
): PointRuleGroup[] {
  const rules = groups[groupIndex]?.rules ?? [];
  const target = ruleIndex + direction;
  if (target < 0 || target >= rules.length) return groups;
  const next = rules.slice();
  [next[ruleIndex], next[target]] = [next[target], next[ruleIndex]];
  return groups.map((group, index) =>
    index === groupIndex ? { ...group, rules: next } : group
  );
}

/**
 * Whether the stored rules moved since `synced`, the copy this page last read
 * or wrote: some other writer (an agent, another tab) wrote them in between.
 * Compared as values, because JSONB hands the document back with its keys
 * reordered. An unreadable server copy is not evidence of anything.
 */
export function changedElsewhere(
  current: PointRuleGroup[] | null,
  synced: PointRuleGroup[] | null
): boolean {
  return Boolean(current && synced && !isEqual(current, synced));
}

/** Groups renumbered by position, which is how the stored document numbers them. */
export function renumber(groups: PointRuleGroup[]): PointRuleGroup[] {
  return groups.map((group, index) => ({ ...group, number: index + 1 }));
}
